import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ratersApi } from "@/api/endpoints";
import type { CarrierQuoteOption, CarrierRaterInfo, MultiCarrierQuoteResponse, ProgressiveRaterStatus, RateShopContext } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { ProgressiveModeBadge, SimulatedBadge, SimulatedPremiumNotice } from "@/components/ProgressiveRaterPanel";
import { formatCurrency } from "@/utils/format";

const PRODUCT_LINES = [
  "Auto",
  "Home",
  "Commercial",
  "General Liability",
  "Workers Comp",
  "Umbrella",
  "RV",
  "Boat",
];

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

export function RateShopPage() {
  const [searchParams] = useSearchParams();
  const leadIdParam = searchParams.get("leadId") ?? undefined;
  const customerIdParam = searchParams.get("customerId") ?? undefined;

  const [context, setContext] = useState<RateShopContext | null>(null);
  const [productLine, setProductLine] = useState("Auto");
  const [state, setState] = useState("TX");
  const [zipCode, setZipCode] = useState("");
  const [vehicleCount, setVehicleCount] = useState(1);
  const [annualMileage, setAnnualMileage] = useState(12000);
  const [priorClaims, setPriorClaims] = useState(0);
  const [carriers, setCarriers] = useState<CarrierRaterInfo[]>([]);
  const [selectedCarrierIds, setSelectedCarrierIds] = useState<Set<string>>(new Set());
  const [loadingCarriers, setLoadingCarriers] = useState(true);
  const [rating, setRating] = useState(false);
  const [result, setResult] = useState<MultiCarrierQuoteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [progressiveStatus, setProgressiveStatus] = useState<ProgressiveRaterStatus | null>(null);

  useEffect(() => {
    ratersApi.progressiveStatus().then(setProgressiveStatus).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!leadIdParam && !customerIdParam) {
      setContext(null);
      return;
    }
    ratersApi
      .context({ leadId: leadIdParam, customerId: customerIdParam })
      .then((ctx) => {
        setContext(ctx);
        if (ctx.product_line) setProductLine(ctx.product_line);
        if (ctx.state) setState(ctx.state);
        if (ctx.zip_code) setZipCode(ctx.zip_code);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load lead context"));
  }, [leadIdParam, customerIdParam]);

  useEffect(() => {
    setLoadingCarriers(true);
    ratersApi
      .listCarriers(productLine)
      .then((data) => {
        setCarriers(data.items);
        const suggested = context?.suggested_carrier_ids ?? [];
        const validSuggested = suggested.filter((id) => data.items.some((item) => item.carrier_id === id));
        if (validSuggested.length > 0) {
          setSelectedCarrierIds(new Set(validSuggested));
        } else {
          setSelectedCarrierIds(new Set(data.items.map((item) => item.carrier_id)));
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load carriers"))
      .finally(() => setLoadingCarriers(false));
  }, [productLine, context?.suggested_carrier_ids]);

  const selectedCount = selectedCarrierIds.size;
  const isAuto = productLine.toLowerCase().includes("auto") || productLine === "RV";
  const linkedLeadId = leadIdParam ?? context?.lead_id ?? undefined;
  const linkedCustomerId = customerIdParam ?? context?.customer_id ?? undefined;

  const sortedOptions = useMemo(() => {
    if (!result) return [];
    return [...result.options].sort((a, b) => {
      if (a.success !== b.success) return a.success ? -1 : 1;
      return a.total_cents - b.total_cents;
    });
  }, [result]);

  function toggleCarrier(carrierId: string) {
    setSelectedCarrierIds((prev) => {
      const next = new Set(prev);
      if (next.has(carrierId)) next.delete(carrierId);
      else next.add(carrierId);
      return next;
    });
  }

  function selectAll() {
    setSelectedCarrierIds(new Set(carriers.map((c) => c.carrier_id)));
  }

  function clearAll() {
    setSelectedCarrierIds(new Set());
  }

  async function runRating() {
    setError(null);
    setMessage(null);
    setResult(null);
    if (selectedCount === 0) {
      setError("Select at least one carrier to rate.");
      return;
    }
    setRating(true);
    try {
      const response = await ratersApi.quote({
        product_line: productLine,
        state,
        zip_code: zipCode || undefined,
        lead_id: linkedLeadId,
        customer_id: linkedCustomerId,
        carrier_ids: Array.from(selectedCarrierIds),
        vehicle_count: isAuto ? vehicleCount : undefined,
        annual_mileage: isAuto ? annualMileage : undefined,
        prior_claims_3yr: priorClaims,
      });
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Rating failed");
    } finally {
      setRating(false);
    }
  }

  async function saveQuote(option: CarrierQuoteOption) {
    if (!result) return;
    setSavingId(option.carrier_id);
    setMessage(null);
    setError(null);
    try {
      const saved = await ratersApi.saveQuote({
        request_id: result.request_id,
        option,
        lead_id: linkedLeadId,
        customer_id: linkedCustomerId,
      });
      setMessage(saved.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSavingId(null);
    }
  }

  const contextLabel = context?.contact_name || context?.company_name;

  return (
    <>
      <PageHeader
        title="Rate Shop"
        subtitle="Compare premiums across carriers. Simulated pricing by default — live carrier quotes only when a carrier is configured for a production rater."
      />
      {progressiveStatus ? (
        <div className="card rate-shop-mode-banner" style={{ padding: "0.85rem 1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
            <div>
              <strong>Progressive:</strong> <ProgressiveModeBadge mode={progressiveStatus.mode} />
              <span style={{ marginLeft: "0.5rem", color: "var(--muted)", fontSize: "0.9rem" }}>
                {progressiveStatus.message}
              </span>
            </div>
            <Link to="/settings" className="btn btn-secondary">
              Configure in Settings
            </Link>
          </div>
        </div>
      ) : null}
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <Link to="/quotes" className="btn btn-secondary">
          View saved quotes
        </Link>
        {linkedLeadId ? (
          <Link to="/pipeline" className="btn btn-secondary">
            Back to pipeline
          </Link>
        ) : null}
        {linkedCustomerId ? (
          <Link to={`/clients/${linkedCustomerId}`} className="btn btn-secondary">
            View client
          </Link>
        ) : null}
      </div>

      {contextLabel ? (
        <div className="card" style={{ padding: "1rem", marginBottom: "1rem", background: "var(--accent-soft)" }}>
          <strong>Rating for {contextLabel}</strong>
          {context?.company_name && context.contact_name ? (
            <div style={{ fontSize: "0.9rem", color: "var(--muted)" }}>{context.company_name}</div>
          ) : null}
          {context?.pipeline_stage ? (
            <div style={{ fontSize: "0.85rem", marginTop: "0.35rem" }}>
              Pipeline stage: {context.pipeline_stage.replace(/_/g, " ")}
            </div>
          ) : null}
          {context?.suggested_carrier_ids.length ? (
            <div style={{ fontSize: "0.85rem", marginTop: "0.35rem" }}>
              Pre-selected {context.suggested_carrier_ids.length} carrier(s) from lead
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="card" style={{ padding: "1rem", marginBottom: "1rem" }}>
        <h3 style={{ marginTop: 0 }}>Risk inputs</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "0.75rem" }}>
          <label>
            Product line
            <select value={productLine} onChange={(e) => setProductLine(e.target.value)}>
              {PRODUCT_LINES.map((line) => (
                <option key={line} value={line}>
                  {line}
                </option>
              ))}
            </select>
          </label>
          <label>
            State
            <select value={state} onChange={(e) => setState(e.target.value)}>
              {US_STATES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
          <label>
            ZIP
            <input value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="78701" maxLength={10} />
          </label>
          {isAuto ? (
            <>
              <label>
                Vehicles
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={vehicleCount}
                  onChange={(e) => setVehicleCount(Number(e.target.value))}
                />
              </label>
              <label>
                Annual mileage
                <input
                  type="number"
                  min={0}
                  value={annualMileage}
                  onChange={(e) => setAnnualMileage(Number(e.target.value))}
                />
              </label>
            </>
          ) : null}
          <label>
            Prior claims (3 yr)
            <input
              type="number"
              min={0}
              max={20}
              value={priorClaims}
              onChange={(e) => setPriorClaims(Number(e.target.value))}
            />
          </label>
        </div>
      </div>

      <div className="card" style={{ padding: "1rem", marginBottom: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
          <h3 style={{ margin: 0 }}>Carriers ({selectedCount} selected)</h3>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button type="button" className="btn btn-secondary" onClick={selectAll} disabled={loadingCarriers}>
              Select all
            </button>
            <button type="button" className="btn btn-secondary" onClick={clearAll} disabled={loadingCarriers}>
              Clear
            </button>
            <button type="button" className="btn btn-primary" onClick={runRating} disabled={rating || loadingCarriers}>
              {rating ? "Rating…" : `Get quotes (${selectedCount})`}
            </button>
          </div>
        </div>
        {loadingCarriers ? (
          <p className="empty-state">Loading carriers…</p>
        ) : carriers.length === 0 ? (
          <p className="empty-state">No carriers support {productLine}.</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.5rem" }}>
            {carriers.map((carrier) => (
              <label
                key={carrier.carrier_id}
                className="kanban-card"
                style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={selectedCarrierIds.has(carrier.carrier_id)}
                  onChange={() => toggleCarrier(carrier.carrier_id)}
                />
                <div>
                  <strong>{carrier.carrier_name}</strong>
                  <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>
                    {carrier.carrier_code} · <ProgressiveModeBadge mode={carrier.mode} />
                  </div>
                  {carrier.supported_lines.length > 0 ? (
                    <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                      {carrier.supported_lines.join(", ")}
                    </div>
                  ) : null}
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {error ? <div className="error-banner">{error}</div> : null}
      {message ? (
        <div className="card" style={{ padding: "0.75rem", marginBottom: "1rem", background: "var(--accent-soft)" }}>
          {message}
        </div>
      ) : null}

      {result ? (
        <div className="card">
          <h3>
            Comparison — {result.carriers_succeeded}/{result.carriers_requested} succeeded
            {result.best_option ? (
              <span style={{ fontWeight: 400, fontSize: "0.9rem", color: "var(--muted)", marginLeft: "0.5rem" }}>
                Best: {result.best_option.carrier_name} at {formatCurrency(result.best_option.total_cents)}
              </span>
            ) : null}
          </h3>
          {sortedOptions.some((option) => option.success && option.is_simulated) ? (
            <SimulatedPremiumNotice style={{ margin: "0.25rem 0 1rem" }} />
          ) : null}
          {sortedOptions.length === 0 ? (
            <p className="empty-state">No quotes returned.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Carrier</th>
                  <th>Premium</th>
                  <th>Fees</th>
                  <th>Taxes</th>
                  <th>Total</th>
                  <th>Tier</th>
                  <th>Ref</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedOptions.map((option, index) => (
                  <tr key={option.carrier_id} style={index === 0 && option.success ? { background: "var(--accent-soft)" } : undefined}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                        <strong>{option.carrier_name}</strong>
                        {option.success && option.is_simulated ? <SimulatedBadge /> : null}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{option.adapter}</div>
                      {!option.success && option.error ? (
                        <div style={{ color: "var(--danger)", fontSize: "0.8rem" }}>{option.error}</div>
                      ) : null}
                    </td>
                    <td>{option.success ? formatCurrency(option.premium_cents) : "—"}</td>
                    <td>{option.success ? formatCurrency(option.fees_cents) : "—"}</td>
                    <td>{option.success ? formatCurrency(option.taxes_cents) : "—"}</td>
                    <td>
                      <strong>{option.success ? formatCurrency(option.total_cents) : "—"}</strong>
                    </td>
                    <td>{option.rating_tier || "—"}</td>
                    <td style={{ fontSize: "0.8rem" }}>{option.quote_reference || "—"}</td>
                    <td>
                      {option.success ? (
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={savingId === option.carrier_id}
                          onClick={() => saveQuote(option)}
                        >
                          {savingId === option.carrier_id ? "Saving…" : linkedLeadId ? "Save to lead" : "Save to CRM"}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
    </>
  );
}
